import * as React from 'react';
import { useField } from 'formik';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../primitives';
import { FormField } from './FormField';
import { cn } from '../../lib/utils';

export interface FormSelectOption {
  label: string;
  value: string;
}

export interface FormSelectProps {
  name: string;
  label?: React.ReactNode;
  options: FormSelectOption[];
  placeholder?: string;
  help?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** Called after the field value is set — for side effects on selection. */
  onValueChange?: (_value: string) => void;
}

/** Formik-bound select built on the design-system Radix `Select`. */
export const FormSelect: React.FC<FormSelectProps> = ({
  name,
  label,
  options,
  placeholder,
  help,
  required,
  disabled,
  id,
  className,
  onValueChange,
}) => {
  const [field, meta, helpers] = useField(name);
  const error = meta.touched ? meta.error : undefined;
  const inputId = id ?? name;

  return (
    <FormField label={label} htmlFor={inputId} required={required} error={error} help={help} className={className}>
      <Select
        value={field.value ?? ''}
        onValueChange={(value) => {
          helpers.setValue(value);
          onValueChange?.(value);
        }}
        disabled={disabled}
      >
        <SelectTrigger
          id={inputId}
          onBlur={() => helpers.setTouched(true)}
          className={cn(error && 'border-destructive focus:ring-destructive/40')}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  );
};
